import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-vs-hex-vs-base32';

const FAQS: Faq[] = [
  { q: 'Which is more compact: Base64, Base32 or hex?', a: 'Base64 is the most compact of the three: it adds about 33% overhead, versus 60% for Base32 and 100% (double size) for hex.' },
  { q: 'Why would I ever use hex instead of Base64?', a: 'Hex is easier to read and debug byte-by-byte (each pair of characters is exactly one byte), and its 16-character alphabet avoids case-sensitivity issues entirely — useful for hashes, colors and memory addresses.' },
  { q: 'Why would I use Base32 instead of Base64?', a: 'Base32 is case-insensitive and excludes visually ambiguous characters (0/O, 1/I/l), making it a better fit for values a human might need to type by hand, like TOTP secret keys or short-lived codes.' },
  { q: 'Is Base32 the same alphabet in every implementation?', a: 'No. RFC 4648 defines a standard Base32 alphabet, but variants exist (e.g. Crockford\'s Base32) with different character sets and padding rules — always check which variant a library uses.' },
];

export default function Base64VsHexVsBase32Page() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Base64 vs Hex vs Base32: Which Encoding Should You Use? | EasyBase64"
        description="Compare Base64, hexadecimal and Base32 encoding — size overhead, alphabet, readability and when to use each, with concrete examples."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Base64 vs Hex vs Base32: Which Encoding Should You Use?',
            description: 'A practical comparison of Base64, hex and Base32 binary-to-text encodings.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 vs Hex vs Base32', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Base64 vs Hex vs Base32: Which Encoding Should You Use?
          </h1>

          <p className={para}>
            Base64, hexadecimal and Base32 all solve the same problem — turning binary data into
            printable text — but they trade off compactness, readability and alphabet size
            differently. Here is how they compare, and how to pick the right one.
          </p>

          <h2 className={heading}>The core tradeoff: alphabet size vs. output size</h2>
          <p className={para}>
            Every binary-to-text scheme packs a fixed number of input bits into each output
            character. Hex uses a 16-character alphabet and packs 4 bits per character, so every
            byte (8 bits) becomes exactly 2 hex characters — a 100% size increase. Base32 uses 32
            characters and packs 5 bits per character, for roughly a 60% increase. Base64 uses 64
            characters and packs 6 bits per character, for about a 33% increase — the smallest of
            the three. See the exact math in{' '}
            <Link to="/base64-file-size-overhead" className="underline">why Base64 makes files ~33% larger</Link>.
          </p>

          <h2 className={heading}>Readability and human-friendliness</h2>
          <p className={para}>
            Hex is the easiest to read at a glance — each byte maps to exactly two characters
            (<code>0-9</code>, <code>a-f</code>), which is why it is the default for hashes
            (MD5, SHA), colors, and memory addresses. Base32 avoids visually ambiguous characters
            like <code>0</code>/<code>O</code> and <code>1</code>/<code>I</code>/<code>l</code>,
            and is case-insensitive, which makes it a good fit for codes a person might type by
            hand — TOTP two-factor secrets and some license keys use Base32 for this reason.
            Base64 is the least human-friendly of the three (mixed case, plus <code>+</code> and{' '}
            <code>/</code> symbols) but wins on compactness, which is why it dominates
            machine-to-machine formats like data URIs, JWTs and API payloads.
          </p>

          <h2 className={heading}>Quick comparison</h2>
          <p className={para}>
            The text <code>Hi</code> (bytes <code>0x48 0x69</code>) encodes as: hex{' '}
            <code>4869</code> (4 characters), Base32 <code>JBQW4===</code> (8 characters with
            padding), Base64 <code>SGk=</code> (4 characters). At this tiny size the difference is
            marginal, but it scales linearly with input — for a 1&nbsp;MB file, Base64 output is
            roughly 250&nbsp;KB smaller than the equivalent Base32 output.
          </p>

          <h2 className={heading}>When to use which</h2>
          <p className={para}>
            Use <strong>hex</strong> for hashes, checksums, colors, and anywhere you or a teammate
            will read the value directly. Use <strong>Base32</strong> for short human-entered
            secrets where case-sensitivity and character ambiguity would cause errors. Use{' '}
            <strong>Base64</strong> (or its{' '}
            <Link to="/url-safe-base64" className="underline">URL-safe variant</Link>) for
            everything else — API payloads, data URIs, tokens, and any binary data that needs to
            move through a text-only channel as compactly as possible. Try it with the{' '}
            <Link to="/" className="underline">Base64 converter</Link> or the{' '}
            <Link to="/hex-to-base64" className="underline">hex to Base64 tool</Link>.
          </p>
        </article>

        <FaqSection title="Base64 vs Hex vs Base32 — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
